<script setup lang="ts">
import {
	computed,
	onMounted,
	ref,
} from "vue";

import { formatTimeToSecond } from "@ca/formatters/dates";
import {
	type UnderwritingAgentTrace,
	UnderwritingAgentTraceSchema,
} from "@ca/types/traces";

import { client } from "@/api";
import { usePersistedAgentTraces } from "@/queries/traces";

type TracePresentation = {
	isDone: boolean;
	statusLabel: string;
	statusIcon: string;
	statusIconClass: string;
	statusBadgeClass: string;
	cardClass: string;
	messageIconClass: string;
};

type TraceTreeNode = TracePresentation & UnderwritingAgentTrace & {
	children: TraceTreeNode[];
};

type SubmissionTraceGroup = {
	submissionId: string;
	parents: TraceTreeNode[];
};

const streamedTraces = ref<UnderwritingAgentTrace[]>([]);
const MAX_TRACES = 1000;

const persistedAgentTracesQuery = usePersistedAgentTraces();

const traces = computed<UnderwritingAgentTrace[]>(() => {
	const dedupedByTraceId = new Map<string, UnderwritingAgentTrace>();
	const persistedTraces = persistedAgentTracesQuery.data.value ?? [];
	for (const trace of persistedTraces) {
		dedupedByTraceId.set(trace.traceId, trace);
	}
	for (const trace of streamedTraces.value) {
		dedupedByTraceId.set(trace.traceId, trace);
	}
	return Array.from(dedupedByTraceId.values())
		.toSorted((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
		.slice(0, MAX_TRACES);
});



const getTracePresentation = (isDone: boolean): TracePresentation => {
	if (isDone) {
		return {
			isDone: true,
			statusLabel: "Done",
			statusIcon: "ic-round-done-all",
			statusIconClass: "text-success-500",
			statusBadgeClass: "text-success-500 bg-success-500/10 border-success-500/30",
			cardClass: "border-success-500/30 bg-success-500/5",
			messageIconClass: "text-success-500",
		};
	}
	return {
		isDone: false,
		statusLabel: "In Progress",
		statusIcon: "ic-round-refresh",
		statusIconClass: "text-info-500",
		statusBadgeClass: "text-info-500 bg-info-500/10 border-info-500/30",
		cardClass: "border-info-500/30 bg-info-500/5",
		messageIconClass: "text-info-500",
	};
};

const shortId = (value: string): string => {
	return value.slice(0, 8);
};

const traceTree = computed<TraceTreeNode[]>(() => {
	const sortedTraces = traces.value.toSorted((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
	const parents = new Map<string, UnderwritingAgentTrace & { children: UnderwritingAgentTrace[] }>();
	const traceIds = new Set(sortedTraces.map((trace) => trace.traceId));
	for (const trace of sortedTraces) {
		const isOrphanChild = trace.parentTraceId !== null && !traceIds.has(trace.parentTraceId);
		if (trace.parentTraceId === null || isOrphanChild) {
			parents.set(trace.traceId, { ...trace, children: [] });
		}
	}
	for (const trace of sortedTraces) {
		if (trace.parentTraceId) {
			const parent = parents.get(trace.parentTraceId);
			if (parent) {
				parent.children.push(trace);
				parents.set(trace.parentTraceId, parent);
			}
		}
	}
	return Array.from(parents.values()).map((parent) => {
		const isDone = parent.final || parent.children.some((child) => child.final);
		return {
			...parent,
			...getTracePresentation(isDone),
			children: parent.children.map((child) => ({
				...child,
				...getTracePresentation(child.final),
				children: [],
			})),
		};
	});
});

const submissionGroups = computed<SubmissionTraceGroup[]>(() => {
	const grouped = new Map<string, TraceTreeNode[]>();
	for (const parent of traceTree.value) {
		const current = grouped.get(parent.submissionId) ?? [];
		current.push(parent);
		grouped.set(parent.submissionId, current);
	}
	const getLatestTimestamp = (parents: TraceTreeNode[]) =>
		Math.max(...parents.map((parent) => new Date(parent.timestamp).getTime()));
	return Array.from(grouped.entries()).map(([submissionId, parents]) => ({
		submissionId,
		parents,
	})).sort((a, b) => getLatestTimestamp(b.parents) - getLatestTimestamp(a.parents));
});

const stream = client.api.v1.agents.traces.subscribe();

onMounted(() => {
	stream.subscribe(({ data }) => {
		const parsed = UnderwritingAgentTraceSchema.safeParse(data);
		if (!parsed.success) {
			console.error("Invalid trace", data, parsed.error);
			return;
		}
		streamedTraces.value.unshift(parsed.data);
		if (streamedTraces.value.length > MAX_TRACES) {
			streamedTraces.value = streamedTraces.value.slice(0, MAX_TRACES);
		}
	});
});
</script>

<template>
  <div class="space-y-4">
    <div
      v-if="submissionGroups.length === 0"
      class="text-sm text-muted "
    >
      Waiting for trace events...
    </div>
    <div
      v-else
      class="space-y-4"
    >
      <div
        v-for="group in submissionGroups"
        :key="group.submissionId"
        class="space-y-2"
      >
        <UCollapsible
          default-open
          class="space-y-2"
        >
          <div class="flex items-center gap-2">
            <UButton
              class="group justify-start"
              color="neutral"
              variant="ghost"
              size="sm"
              trailing-icon="i-lucide-chevron-down"
              :title="group.submissionId"
              :ui="{
                trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform duration-200'
              }"
            >
              <span class="text-sm font-semibold truncate">
                Submission: {{ shortId(group.submissionId) }}
              </span>
            </UButton>

            <UButton
              trailing-icon="ic-round-open-in-new"
              variant="link"
              size="xs"
              :to="`/submissions/${group.submissionId}`"
            />
          </div>
          <template #content>
            <div class="ml-2 mt-2 border-l border-default/60 pl-3 space-y-2">
              <div
                v-for="parent in group.parents"
                :key="parent.traceId"
                class="rounded-xl border p-3.5 shadow-sm"
                :class="parent.cardClass"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0 space-y-1">
                    <div class="flex items-center gap-2">
                      <UIcon
                        :name="parent.statusIcon"
                        class="size-4 shrink-0"
                        :class="[
                          parent.statusIconClass,
                          parent.isDone ? '' : 'animate-spin',
                        ]"
                      />
                      <div class="text-sm font-semibold truncate">
                        {{ parent.taskSlug }}
                      </div>
                      <div
                        class="text-[11px] px-2 py-0.5 rounded-full border font-medium"
                        :class="parent.statusBadgeClass"
                      >
                        {{ parent.statusLabel }}
                      </div>
                    </div>
                    <div
                      class="text-xs text-muted flex items-center gap-1.5"
                    >
                      <UIcon
                        name="i-ic-baseline-track-changes"
                        class="size-3.5 text-primary-500"
                      />
                      <span class="truncate">
                        {{ parent.intent ?? "No intent provided" }}
                      </span>
                    </div>
                  </div>
                  <div class="text-xs text-muted whitespace-nowrap">
                    {{ formatTimeToSecond(parent.timestamp) }}
                  </div>
                </div>

                <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div class="rounded-md border border-default/60 px-2 py-1.5 bg-default/20 flex items-center gap-1.5">
                    <UIcon
                      name="i-ic-baseline-fingerprint"
                      class="size-3.5 text-muted"
                    />
                    <span class="text-muted">trace</span>
                    <span
                      class="font-mono"
                      :title="parent.traceId"
                    >{{ shortId(parent.traceId) }}</span>
                  </div>

                  <div class="rounded-md border border-default/60 px-2 py-1.5 bg-default/20 flex items-center gap-1.5">
                    <UIcon
                      name="i-ic-baseline-description"
                      class="size-3.5 text-muted"
                    />
                    <span class="text-muted">submission</span>
                    <span
                      class="font-mono"
                      :title="parent.submissionId"
                    >{{ shortId(parent.submissionId) }}</span>
                  </div>
                </div>

                <div
                  v-if="parent.children.length > 0"
                  class="mt-3 pt-3 border-t border-default/60 space-y-2"
                >
                  <div class="text-xs uppercase tracking-wide text-muted font-medium">
                    Sub-Traces ({{ parent.children.length }})
                  </div>
                  <ul class="space-y-2">
                    <li
                      v-for="child in parent.children"
                      :key="child.traceId"
                      class="rounded-md border border-default/60 bg-default/20 px-2.5 py-2"
                    >
                      <div class="flex items-start justify-between gap-2">
                        <div class="min-w-0 flex items-center gap-2">
                          <UIcon
                            name="ic-round-check-circle"
                            class="size-3.5 shrink-0"
                            :class="parent.statusIconClass"
                          />
                          <div class="text-xs font-semibold truncate">
                            {{ child.taskSlug }}
                          </div>
                          <div
                            class="text-[10px] px-1.5 py-0.5 rounded-full border font-medium"
                            :class="parent.statusBadgeClass"
                          >
                            Done
                          </div>
                        </div>
                        <div class="text-[11px] text-muted whitespace-nowrap">
                          {{ formatTimeToSecond(child.timestamp) }}
                        </div>
                      </div>
                      <ul class="mt-2 space-y-1.5 border-l border-default/60 pl-2.5">
                        <li class="flex items-start gap-2">
                          <UIcon
                            name="i-ic-baseline-chat"
                            class="size-3.5 mt-0.5 shrink-0"
                            :class="parent.messageIconClass"
                          />
                          <div class="min-w-0">
                            <div class="text-xs font-medium wrap-break-word">
                              {{ child.message || "No message" }}
                            </div>
                          </div>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </template>
        </UCollapsible>
      </div>
    </div>
  </div>
</template>
